import {
  useRTCClient,
  useClientEvent,
  useRemoteUsers,
} from 'agora-rtc-react';
import { useCallback, useMemo, useState } from 'react';

import Button from "@ergeon/core-components/exports/Button.ts";

import { PhoneXMarkIcon } from "@heroicons/react/24/outline";

import ButtonCamera from '@/app/components/ButtonCamera';
import ButtonMicrophone from '@/app/components/ButtonMicrophone';
import ButtonScreenShare from '@/app/components/ButtonScreenShare';
import ButtonSettings from '@/app/components/ButtonSettings';
import ButtonSwitchCamera from '@/app/components/ButtonSwitchCamera';
import ButtonVirtualBackground from '@/app/components/ButtonVirtualBackground';
import { ScreenPreloader } from '@/app/components/Preloader';
import RecordingBadge from '@/app/components/RecordingBadge';
import Title from '@/app/components/Title';
import VideoTracksDisplay from '@/app/components/VideoTracksDisplay';
import { useAIDenoiserExtension } from '@/app/extensions/AIDenoiser/context';
import { useMeetingData, useUserData } from '@/app/queries/MeetingQuery/context';
import useIsCompactViewport from '@/app/hooks/useIsCompactViewport';
import useOverlayVisibility from '@/app/hooks/useOverlayVisibility';
import useSoundEffects from '@/app/hooks/useSoundEffects';
import { cn } from '@/lib/utils';

import './MeetingView.css';

import { useAppContext } from '@/app/AppContext';

import usePublish from '@/app/hooks/usePublish';
import useAutoAdjustStreamQuality from '@/app/hooks/useAutoAdjustStreamQuality';
import AnalyticsSync from '@/app/analytics/components/AnalyticsSync';

import DialogFeedback from '@/app/components/DialogFeedback';

interface MeetingViewProps {
  // Tears the meeting down, once the feedback dialog is done.
  onEndCall(): void;
  // Fired first, the moment the user clicks "End call", so the host can promptly
  // wind up recording/transcription while both sides are still in the channel.
  // See MeetingPage.
  onBeforeEndCall(): void;
}

export default function MeetingView({ onEndCall, onBeforeEndCall }: MeetingViewProps) {
  const [showVideoTracks, setShowVideoTracks] = useState(true);
  const { isVisible, reveal } = useOverlayVisibility();
  const isCompact = useIsCompactViewport();
  const userData = useUserData();

  const remoteUsers = useRemoteUsers();
  const { playJoinSound } = useSoundEffects();
  const { participants } = useMeetingData();

  const knownUids = useMemo(() => new Set(
    participants.flatMap(p => [p.user_id, p.screen_share_id])
  ), [participants]);

  const agoraClient = useRTCClient();

  useAutoAdjustStreamQuality(agoraClient);

  useClientEvent(agoraClient, 'user-joined', (user) => {
    if (knownUids.has(user.uid as number)) {
      playJoinSound();
    }
  });

  const { camera, microphone } = useAppContext();

  const onEndCallHandler = useCallback(() => {
    // Signal the host-side recording lifecycle to end promptly (see MeetingPage),
    // then release the devices for real, even though the page only unmounts
    // once the feedback dialog is done. Teardown is not a toggle: the
    // microphone may sit soft-muted, and a muted track rejects setEnabled
    // outright (TRACK_STATE_UNREACHABLE) — closing it is legal in every mute
    // state, and it lets the hardware and the browser's recording indicator go.
    onBeforeEndCall();
    if (microphone.track?.isPlaying) {
      microphone.track.stop();
    }
    microphone.track?.close();
    if (camera.track?.isPlaying) {
      camera.track.stop();
    }
    camera.track?.setEnabled(false).catch(() => undefined);
    setShowVideoTracks(false);
  }, [microphone, camera, onBeforeEndCall]);

  const isCameraReady = !(camera.isLoading || camera.error);
  const isMicReady = !(microphone.isLoading || microphone.error);

  const isLoading = camera.isLoading || microphone.isLoading;

  usePublish(microphone.track, isMicReady);
  usePublish(camera.track, isCameraReady);

  useAIDenoiserExtension(
    { track: microphone.track },
    userData.isClient && !isMicReady
  );

  if (isLoading) {
    return <ScreenPreloader />;
  }

  // Floating over the video, the overlays need z-30 to clear the layer the
  // Agora SDK puts over a remote video (it wraps the track children in a
  // full-size z-index: 2 box), or the controls are visible but unclickable.
  const overlayClassName = 'absolute inset-x-0 z-30 flex px-4 md:static';

  // Header and controls both get out of the way, and both come back on a tap
  // anywhere. On a phone the screen is small and often in the sun, and whatever
  // sits on top of the fence distracts the person walking it — so the account
  // manager asks for a light tap in the middle of the screen instead, and the
  // buttons return. useOverlayVisibility only lets isVisible go false on a
  // phone, so this needs no breakpoint escape of its own.
  //
  // Pointer events leave with the opacity: the tap that summons a control back
  // must not press it while it is still invisible.
  const fadesWhenIdle = cn(
    'transition-opacity duration-300',
    { 'pointer-events-none opacity-0': !isVisible },
  );

  return (
    <div
      className="dark fixed inset-0 h-dvh overflow-hidden bg-gray-900 md:flex md:flex-col"
      onPointerDown={reveal}
      onPointerMove={reveal}>
      <AnalyticsSync isCallLive={showVideoTracks} />

      {/* Nothing in the header can be clicked, and on a phone it hangs 88px
          over the participant tiles beneath it. Left live it would swallow
          taps meant for the tile — which is how "tapping a tile does nothing"
          started. */}
      <header className={cn(overlayClassName, fadesWhenIdle, 'pointer-events-none top-0 items-center gap-3 bg-gradient-to-b from-black/70 to-transparent pb-10 pt-safe-top md:bg-none md:py-3')}>
        <Title />
        <RecordingBadge />
      </header>

      <VideoTracksDisplay {...{ localCameraTrack: camera.track!, remoteUsers, showVideoTracks }} />

      <div className={cn(overlayClassName, fadesWhenIdle, 'bottom-0 items-center justify-center gap-3 pb-safe-bottom pt-12 md:py-4')}>
        {/* The scrim stays with the buttons it exists to keep legible over a
            sunlit fence. */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 to-transparent md:hidden" />
        {/* On a phone the bar is left to the two controls a call actually turns
            on — flipping to the rear camera, which the account manager talks
            the customer through by colour, and hanging up. The devices you set
            once and forget stack into the corner just above it. */}
        {/* Held in a column, and laid on its side in landscape, where a phone
            has barely 375px of height and a column would climb into the
            participant tiles. */}
        {isCompact && (
          <div className="absolute bottom-full right-4 mb-3 flex flex-col gap-3 landscape:flex-row">
            <ButtonMicrophone />
            <ButtonCamera />
            <ButtonSettings />
          </div>
        )}
        <div className="relative flex items-center gap-3">
          {userData.isClient && <ButtonSwitchCamera />}
          {!isCompact && <><ButtonMicrophone /><ButtonCamera /></>}
          {userData.isHost &&
            <>
              <ButtonScreenShare />
              <ButtonVirtualBackground localCameraTrack={camera.track!} />
            </>
          }
          {!isCompact && <ButtonSettings />}
        </div>
        {/* Pinned to the edge on a phone rather than sitting next to the blue
            button the account manager tells the customer to press: hanging up
            by accident costs them the call. */}
        <div className={isCompact ? 'absolute right-4' : 'relative ml-3'}>
          <DialogFeedback onEndCall={onEndCall}>
            <Button
              disabled={!showVideoTracks}
              flavor="danger"
              isSquare
              onClick={onEndCallHandler}
              shape="circle"
              size="large"
              title="End call">
              <PhoneXMarkIcon className="size-6" />
            </Button>
          </DialogFeedback>
        </div>
      </div>
    </div>
  );
}
